import { isEmptyValue, normalizeSheetRows } from "./payloadRows";

export type VendorContactRecord = {
  "Contact Type": string;
  Value: string | null;
};

const isNormalizedVendorRecord = (item: Record<string, unknown>) =>
  Array.isArray(item.contacts);

const extractVendorContactFromFlatRecord = (
  item: Record<string, unknown>,
): VendorContactRecord | null => {
  const type = item["Contact Type"];
  const value = item.Value ?? item.Email ?? item.Mobile ?? null;

  if (typeof type !== "string" || isEmptyValue(value)) {
    return null;
  }

  return { "Contact Type": type, Value: String(value) };
};

const stripContactFields = (
  item: Record<string, unknown>,
): Record<string, unknown> => {
  const {
    "Contact Type": _contactType,
    Value: _value,
    Email: _email,
    Mobile: _mobile,
    contacts: _contacts,
    ...vendor
  } = item;

  return vendor;
};

/** Converts legacy flat contact rows into one vendor record with a contacts array. */
export const normalizeVendorSetupPayload = (
  items: unknown[],
): Record<string, unknown>[] => {
  if (!Array.isArray(items) || items.length === 0) {
    return [];
  }

  const normalized: Record<string, unknown>[] = [];
  const flatGroups = new Map<string, Record<string, unknown>>();

  for (const raw of items) {
    if (!raw || typeof raw !== "object") {
      continue;
    }

    const item = raw as Record<string, unknown>;

    if (isNormalizedVendorRecord(item)) {
      normalized.push({
        ...stripContactFields(item),
        contacts: item.contacts ?? [],
      });
      continue;
    }

    const vendorCode = String(item["Vendor Code"] ?? "");
    if (!vendorCode) {
      continue;
    }

    if (!flatGroups.has(vendorCode)) {
      flatGroups.set(vendorCode, {
        ...stripContactFields(item),
        contacts: [] as VendorContactRecord[],
      });
    }

    const vendor = flatGroups.get(vendorCode)!;
    const contact = extractVendorContactFromFlatRecord(item);

    if (contact) {
      (vendor.contacts as VendorContactRecord[]).push(contact);
    }
  }

  return [...normalized, ...Array.from(flatGroups.values())];
};

export const mapVendorSetupVendorRows = (
  sheetData: unknown,
): Record<string, unknown>[] =>
  normalizeVendorSetupPayload(normalizeSheetRows(sheetData));

export const mapVendorSetupContactRows = (
  sheetData: unknown,
): Record<string, unknown>[] => {
  const vendors = normalizeVendorSetupPayload(normalizeSheetRows(sheetData));
  const rows: Record<string, unknown>[] = [];

  for (const vendor of vendors) {
    const contacts =
      (vendor.contacts as VendorContactRecord[] | undefined) ?? [];

    for (const contact of contacts) {
      if (isEmptyValue(contact.Value)) {
        continue;
      }

      rows.push({
        "Vendor Code": vendor["Vendor Code"],
        "Is Primary": vendor["Is Primary"],
        "Is Private Info": vendor["Is Private Info"],
        "Contact Type": contact["Contact Type"],
        Value: contact.Value,
      });
    }
  }

  return rows;
};
